/* eslint-disable simple-import-sort/imports */
import React, { FC } from 'react';
import { Outlet, Route, Routes } from 'react-router-dom';

// providers, data setup, and other necessary pos globals
import { AuthRequestUIBlocker } from 'Src/feature/auth-request/AuthRequestUIBlocker';
import useRoute from 'Src/routes/useRoute';
import { CurrentCheckRouter } from 'Src/feature/check/CheckRouter';
import { CheckListEditTips } from 'Src/feature/check-list/CheckListEditTips';
import { useCurrentAuthRequest } from 'Src/feature/auth-request/useCurrentAuthRequest';
import { CardReaderConnect } from 'Src/feature/card-reader/CardReaderConnect';
import { SettingsLatencyTest } from 'Src/feature/settings/SettingsLatencyTest';
import { PaymentFlowProvider } from 'Src/feature/payment/usePaymentFlow';
import ComingSoonPage from 'Src/components/ComingSoonPage';
import CashBankList from 'Src/feature/cash-bank/CashBankList';
import CashBankView from 'Src/feature/cash-bank/CashBankView';
import ChecksPage from 'Src/feature/checks/ChecksPage';
import FlipAndTipEndPage from 'Src/feature/flip-and-tip/FlipAndTipEndPage';
import FlipAndTipStartPage from 'Src/feature/flip-and-tip/FlipAndTipStartPage';
import PayInOutPage from 'Src/feature/pay-in-out/PayInOutPage';
import SplitPayment from 'Src/feature/payment/SplitPayment';
import POSBeginBreak from 'Src/feature/pos/POSBeginBreak';
import POSClockout from 'Src/feature/pos/POSClockout';
import POSLogout from 'Src/feature/pos/POSLogout';
import SettingsCardReader from 'Src/feature/settings/SettingsCardReader';
import SettingsPrinters from 'Src/feature/settings/SettingsPrinters';
import SettingsPage from 'Src/feature/settings/SettingsPage';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sectionId = 'POSRouter';

const POSRouter: FC = () => {
  const { crumbs } = useRoute();
  const { currentRequest: currentAuthRequest } = useCurrentAuthRequest();

  return (
    <>
      {/* TODO: If terminal is time-clock-only terminal, we should have pared-down or different routes */}
      <CardReaderConnect />
      <AuthRequestUIBlocker key={currentAuthRequest?.id} />
      <Routes>
        <Route path={crumbs.pos.landing.pathFrom({ part: 'pos', absolute: true })} element={<ComingSoonPage />} />
        <Route path={crumbs.pos.payInOut.main.pathFrom({ part: 'pos', absolute: true })} element={<PayInOutPage />} />
        <Route path={crumbs.pos.tipEdit.main.pathFrom({ part: 'pos', absolute: true })} element={<CheckListEditTips />} />

        {/*
            each "current" check route used w/ same "CurrentCheckRouter" component
            - all "current" check routes have a "checkId" route param
            - "CurrentCheckRouter" will stay mounted while on any "current" check route
            - "CurrentCheckRouter" will stay unmount when moving off of any "current" check route
            - this allows for passing route params values through to "CurrentCheckRouter" & it's children
        */}
        <Route
          element={(
            <PaymentFlowProvider>
              <Outlet />
            </PaymentFlowProvider>
          )}
        >
          <Route path={crumbs.pos.check.detail.pathFrom({ part: 'pos' })} element={<CurrentCheckRouter />} />
          <Route path={crumbs.pos.check.item.new.pathFrom({ part: 'pos' })} element={<CurrentCheckRouter />} />
          <Route path={crumbs.pos.check.item.edit.pathFrom({ part: 'pos' })} element={<CurrentCheckRouter />} />
          {/* same view for below 2 w/ previous architecture */}
          <Route path={crumbs.pos.check.payment.main.pathFrom({ part: 'pos' })} element={<CurrentCheckRouter />} />
          <Route path={crumbs.pos.check.split.main.pathFrom({ part: 'pos' })} element={<CurrentCheckRouter />} />
          <Route path={crumbs.pos.check.transfer.main.pathFrom({ part: 'pos' })} element={<CurrentCheckRouter />} />

          <Route path={crumbs.pos.customer.flipAndTip.start.pathFrom({ part: 'pos' })} element={<FlipAndTipStartPage />} />
          <Route path={crumbs.pos.customer.flipAndTip.end.pathFrom({ part: 'pos' })} element={<FlipAndTipEndPage />} />

          {/* fall back for any other check routes */}
          <Route path={crumbs.pos.check.main.pathFrom({ part: 'pos', wildCard: true })} element={<CurrentCheckRouter />} />
        </Route>

        <Route path={crumbs.pos.checks.main.pathFrom({ part: 'pos' })} element={<ChecksPage />} />

        <Route path="/cash-bank-list" element={<CashBankList />} />
        <Route path="/cash-bank/:cashBankId" element={<CashBankView />} />
        <Route path="/user/begin-break" element={<POSBeginBreak />} />
        <Route path="/user/clockout" element={<POSClockout />} />
        <Route path="/user/logout" element={<POSLogout />} />
        <Route path="/split-payment" element={<SplitPayment />} />
        <Route path="/settings/*" element={<SettingsPage />} >
          <Route path="printers" element={<SettingsPrinters />} />
          <Route path="card-reader" element={<SettingsCardReader />} />
          <Route path="network-test" element={<SettingsLatencyTest />} />
        </Route>
      </Routes>
    </>
  );
};

export default POSRouter;
